import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { AlertCircleIcon, Loader2Icon, RotateCcwIcon, SearchIcon, SearchXIcon, XIcon } from 'lucide-react';
import { CensusPagination } from './CensusPagination';
import { CensusRow } from './CensusRow';
import { useCensus } from '../hooks/useCensus';
import { formatNumber, normalizeText, onlyDigits } from '../utils/text';
const PAGE_SIZE = 10;
export function CensusSearch() {
  const {
    status,
    records,
    retry
  } = useCensus();
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const deferredQuery = useDeferredValue(query);
  const results = useMemo(() => {
    const text = normalizeText(deferredQuery);
    if (!text) return records;
    const digits = onlyDigits(deferredQuery);
    const words = text.split(' ');
    return records.filter(r => words.every(w => r.searchText.includes(w)) || digits.length >= 3 && r.searchDigits.includes(digits));
  }, [records, deferredQuery]);
  useEffect(() => setPage(1), [deferredQuery]);
  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const start = (current - 1) * PAGE_SIZE;
  const visible = results.slice(start, start + PAGE_SIZE);
  const hasQuery = deferredQuery.trim().length > 0;
  return <div className="overflow-hidden rounded-2xl bg-white shadow-md ring-1 ring-primary/10" data-id="element-168" data-mp="2vw8316ofqn5q">
      {/* Search bar */}
      <div className="border-b border-primary/10 p-4 sm:p-5" data-id="element-169" data-mp="3ash0c2ormqfi">
        <label htmlFor="census-search" className="text-sm font-bold text-primary" data-id="element-170" data-mp="35uxfgjxkbfel">
          Busca por razón social, NIT, matrícula, representante legal o correo
        </label>
        <div className="relative mt-2" data-id="element-171" data-mp="3jnwqa8meglft">
          <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-primary/50" data-id="element-172" data-mp="3fk8l1wl8q1xq" />
          <input id="census-search" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Ej: Grupo Contable Asesores o 900123456" autoComplete="off" disabled={status !== 'ready'} className="h-14 w-full rounded-xl border-2 border-primary/15 bg-[#F8FAFD] pl-12 pr-12 text-base text-foreground outline-none transition placeholder:text-foreground/40 focus:border-primary focus:bg-white disabled:opacity-60 [&::-webkit-search-cancel-button]:hidden" data-id="element-173" data-mp="3tkrnmo6xsfsi" />
          {query && <button type="button" onClick={() => setQuery('')} aria-label="Limpiar búsqueda" className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-foreground/50 transition hover:bg-primary/5 hover:text-primary" data-id="element-174" data-mp="34xqmgdb366a5">
              <XIcon className="h-4 w-4" data-id="element-175" data-mp="1jl5kcxdfhmvt" />
            </button>}
        </div>
        {status === 'ready' && <p className="mt-2 text-sm text-foreground/60" aria-live="polite" data-id="element-176" data-mp="1oj73tjerno7t">
            {hasQuery ? <>
                <strong className="text-primary" data-id="element-177" data-mp="1h1960c5y61kf">{formatNumber(results.length)}</strong>{' '}
                {results.length === 1 ? 'resultado' : 'resultados'} de {formatNumber(records.length)} empresas habilitadas
              </> : <>
                <strong className="text-primary" data-id="element-178" data-mp="12c394uul0nfp">{formatNumber(records.length)}</strong> empresas habilitadas para votar
              </>}
          </p>}
      </div>

      {/* Body */}
      {status === 'loading' && <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-primary/70" data-id="element-179" data-mp="1v0s3mb14tuih">
          <Loader2Icon className="h-8 w-8 animate-spin" data-id="element-180" data-mp="1pzci6hv1llzk" />
          <p className="text-sm font-semibold" data-id="element-181" data-mp="1m03spp71ca9t">Cargando el censo electoral…</p>
        </div>}

      {status === 'error' && <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center" data-id="element-182" data-mp="enk3yo6twqru">
          <AlertCircleIcon className="h-9 w-9 text-secondary-maroon" data-id="element-183" data-mp="aiu7oy89da6e" />
          <p className="font-bold text-primary" data-id="element-184" data-mp="1ygtlj5qqkdp5">No pudimos cargar el censo</p>
          <p className="max-w-sm text-sm text-foreground/60" data-id="element-185" data-mp="237jsuoiq2qmh">Revisa tu conexión e inténtalo de nuevo.</p>
          <button type="button" onClick={retry} className="mt-2 inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-bold text-white transition hover:bg-[#14307A]" data-id="element-186" data-mp="1oj71y5yhrxyu">
            <RotateCcwIcon className="h-4 w-4" data-id="element-187" data-mp="2h8w4wouri833" /> Reintentar
          </button>
        </div>}

      {status === 'ready' && results.length === 0 && <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center" data-id="element-188" data-mp="2876ffokfxj7y">
          <SearchXIcon className="h-9 w-9 text-primary/40" data-id="element-189" data-mp="2c8881mjuqx0y" />
          <p className="font-bold text-primary" data-id="element-190" data-mp="2c6orrvy74lz6">No encontramos “{deferredQuery.trim()}” en el censo</p>
          <p className="max-w-md text-sm text-foreground/60" data-id="element-191" data-mp="wtu99717koim">
            Verifica la escritura o busca por NIT sin dígito de verificación. Si tu empresa no aparece, comunícate con la
            Cámara de Comercio de Cali.
          </p>
        </div>}

      {status === 'ready' && results.length > 0 && <>
          {/* Desktop header */}
          <div className="hidden grid-cols-[minmax(0,2.2fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,1.6fr)_minmax(0,1.7fr)_auto] gap-4 bg-[#F4F7FC] px-5 py-3 text-xs font-bold uppercase tracking-wider text-primary/70 lg:grid" data-id="element-192" data-mp="136ygyltbqp7i">
            <span data-id="element-193" data-mp="ajuaecel22tm">Razón social</span>
            <span data-id="element-194" data-mp="5k9b20vi3sgd">NIT / Identificación</span>
            <span data-id="element-195" data-mp="5lkajohwye1o">Matrícula</span>
            <span data-id="element-196" data-mp="5i4xer7svf2w">Representante legal</span>
            <span data-id="element-197" data-mp="k9dqxbetpklb">Correo comercial</span>
            <span className="w-28" data-id="element-198" data-mp="kagizmnvtt2t">Estado</span>
          </div>
          <ul data-id="element-199" data-mp="fi5k8n4vodbu">
            {visible.map(r => <CensusRow key={r.id} record={r} data-id="element-200" data-mp="fgkncqv3ciat" />)}
          </ul>
          <div className="flex flex-col items-center justify-between gap-3 border-t border-primary/10 bg-[#F8FAFD] px-5 py-4 sm:flex-row" data-id="element-201" data-mp="3nwe3h4lifrrc">
            <p className="text-sm text-foreground/60 tabular-nums" data-id="element-202" data-mp="ci5b4msudkki">
              Mostrando {formatNumber(start + 1)}–{formatNumber(start + visible.length)} de {formatNumber(results.length)}
            </p>
            <CensusPagination page={current} totalPages={totalPages} onChange={setPage} data-id="element-203" data-mp="lagx9wgxaluo" />
          </div>
        </>}
    </div>;
}